import { Popup } from "@progress/kendo-react-popup";
import { useCallback, useEffect, useRef, useState } from "react";
import { familles } from "../../configs/listview/Colors";
import { FaEyeDropper } from "react-icons/fa";
import { Button } from "@progress/kendo-react-buttons";
import useEyeDropper from "use-eye-dropper";
import "./circlecolor.css";

interface CircleColorProps {
    colors: { familleWeb: string, familleWebSource: string, swatchHex1: string, swatchHex2: string };
    editable?: boolean;
    editMode?: string;
    onChange1?: any;
    onChange2?: any;
    disabled?: boolean;
}

export default function CircleColor(props: CircleColorProps) {
    const anchor = useRef<HTMLDivElement | null>(null);
    const popupRef = useRef<any>(null);
    const [currentSwatch1, setCurrentSwatch1] = useState<string>(props.colors.swatchHex1);
    const [currentSwatch2, setCurrentSwatch2] = useState<string>(props.colors.swatchHex2);
    const [show, setShow] = useState(false);
    const onClick = () => {
        if ((isFamilleUpdatable || isEchantillon) && !props.disabled)
            setShow(!show);
    };
    const isFamille = props.editMode === "famille";
    const isEchantillon = props.editMode === "echantillon";
    const isFamilleUpdatable = isFamille && props.colors.familleWebSource === "produit";
    const { open } = useEyeDropper();

    const openPicker = useCallback(async (setState: React.Dispatch<string>) => {
        try {
            const color = await open();
            setState(color.sRGBHex);
        } catch (e) {
            console.log(e);
        }
    }, [open]);

    useEffect(() => {
        function handleClickOutside(event: any) {
            if (popupRef.current && !popupRef.current.contains(event.target) && anchor.current && !anchor.current.contains(event.target)) {
                setShow(_show => false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [anchor]);

    useEffect(() => {
        setCurrentSwatch1(props.colors.swatchHex1);
        setCurrentSwatch2(props.colors.swatchHex2);
    }, [props.colors]);

    const getBackground = (colors: string[]) => {
        if (colors.length === 1) {
            if (colors[0] === 'multicolor')
                return { background: 'linear-gradient(90deg, rgba(255,0,0,1) 0%, rgba(255,154,0,1) 10%, rgba(208,222,33,1) 20%, rgba(79,220,74,1) 30%, rgba(63,218,216,1) 40%, rgba(47,201,226,1) 50%, rgba(28,127,238,1) 60%, rgba(95,21,242,1) 70%, rgba(186,12,248,1) 80%, rgba(251,7,217,1) 90%, rgba(255,0,0,1) 100%)' }
            else
                return { backgroundColor: colors[0] }
        } else if (colors.length === 2)
            return { background: `linear-gradient(90deg, ${colors[0]} 50%, ${colors[1]} 50%)` }
        else
            return { background: 'repeating-linear-gradient(90deg, transparent, transparent 5px, #ccc 5px, #ccc 10px), linear-gradient(to bottom, #eee, #eee)' };
    }

    const getCircleColor = () => {
        let colors = [] as string[];
        if (isFamille) {
            colors = [familles.find(f => f.name === props.colors.familleWeb) ? familles.find(f => f.name === props.colors.familleWeb)!.code : ""];
        } else if (isEchantillon) {
            colors = [...currentSwatch1 ? [currentSwatch1] : [], ...currentSwatch2 ? [currentSwatch2] : []]
        }

        return getBackground(colors);
    };

    const updateSwatch1 = (swatch1: string) => {
        setCurrentSwatch1(swatch1);
        props.onChange1(swatch1);
    }

    const updateSwatch2 = (swatch2: string) => {
        setCurrentSwatch2(swatch2);
        props.onChange2(swatch2);
    }

    const handleArrowClick = () => {
        updateSwatch1(currentSwatch2);
        updateSwatch2(currentSwatch1);
    };

    return (<>
        <div ref={anchor} onClick={onClick} className={`circle-color ${props.editable && (isFamilleUpdatable || isEchantillon) && !props.disabled ? 'k-cursor-pointer' : ''}`} style={{ ...getCircleColor(), margin: '0 auto' }}>
            {isFamille && !isFamilleUpdatable && (<span id="lock-icon" className="k-icon k-font-icon k-i-lock k-icon-lg" />)}
        </div>
        {props.editable && isFamille && (<Popup anchor={anchor.current} show={show} className={"famille-popup"} anchorAlign={{ horizontal: 'center', vertical: 'bottom' }} popupAlign={{ horizontal: 'center', vertical: 'top' }}>
            <div className="famille-wrapper" ref={popupRef}>
                {familles.map((f, i) =>
                    <div className="famille-content" key={i} onClick={() => { props.onChange1(f.name); setShow(false) }}>
                        <div style={{ ...getBackground([f.code]) }} className="circle-color k-cursor-pointer">
                        </div>
                        <label>{f.title}</label>
                    </div>)}
            </div>
            <Button icon="close" className="couleur-close-button" />
        </Popup>)}
        {props.editable && isEchantillon && (<Popup anchor={anchor.current} show={show} anchorAlign={{ horizontal: 'center', vertical: 'bottom' }} popupAlign={{ horizontal: 'center', vertical: 'top' }}>
            <div className="echantillon-wrapper" ref={popupRef}>
                <div className="echantillon-content">
                    <span>Couleur 1</span>
                    <div className="echantillon-popup-swatch" style={{ background: currentSwatch1 ? currentSwatch1 : 'repeating-linear-gradient(45deg, transparent, transparent 5px, #ccc 5px, #ccc 10px), linear-gradient(to bottom, #eee, #eee)' }} />
                    <FaEyeDropper className="k-cursor-pointer" onClick={() => openPicker(updateSwatch1)} />
                    <Button className="blueButton effacer-button" onClick={() => updateSwatch1("")}>Effacer</Button>
                </div>
                <span className="k-icon k-font-icon k-i-arrows-swap k-rotate-90 k-icon-xxl k-cursor-pointer" onClick={handleArrowClick}></span>
                <div className="echantillon-content">
                    <span>Couleur 2</span>
                    <div className="echantillon-popup-swatch" style={{ background: currentSwatch2 ? currentSwatch2 : 'repeating-linear-gradient(45deg, transparent, transparent 5px, #ccc 5px, #ccc 10px), linear-gradient(to bottom, #eee, #eee)' }} />
                    <FaEyeDropper className="k-cursor-pointer" onClick={() => openPicker(updateSwatch2)} />
                    <Button className="blueButton effacer-button" onClick={() => updateSwatch2("")}>Effacer</Button>
                </div>
            </div>
            <Button icon="close" className="couleur-close-button" />
        </Popup>)}
    </>)
}